import { RiskFullNameDCR } from '@ddt-web/config';
import { LodashExtractor, StoreNestedFilterExtractor } from '@ddt-web/data-service';

import { FactMappingInterface } from '../../../models/fact-mapping.interface';
import { FactExtractor } from '../fact-extractor';

export const RISK_FACTS: { [factName: string]: FactMappingInterface } = {
	spreadRisk: {
		extractor: new FactExtractor(
			[
				new StoreNestedFilterExtractor(
					['risk'],
					'risk.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'fixedIncome', desiredValueExpression: 'sleeveAnalysis.risk.factorContributors' }
					]
				),
				new StoreNestedFilterExtractor(
					['risk'],
					'risk.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'fixedIncome', desiredValueExpression: 'sleeveAnalysis.risk.total' }
					]
				)
			],
			(factorContributors, totalRisk) => {
				return factorContributors.reduce((sum, data) => {
					return (
						sum +
						(['spreads investment grade', 'spreads high yield', 'spreads municipal', 'spreads em', 'spreads securitized', 'spreads loans - credit'].includes(
							data.name.toLowerCase()
						)
							? (data.contribution / totalRisk) * 100
							: 0)
					);
				}, 0);
			}
		)
	},
	rateRisk: {
		extractor: new FactExtractor(
			[
				new StoreNestedFilterExtractor(
					['risk'],
					'risk.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'fixedIncome', desiredValueExpression: 'sleeveAnalysis.risk.factorContributors' }
					]
				),
				new StoreNestedFilterExtractor(
					['risk'],
					'risk.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'fixedIncome', desiredValueExpression: 'sleeveAnalysis.risk.total' }
					]
				)
			],
			(factorContributors, totalRisk) => {
				return factorContributors.reduce((sum, data) => {
					return sum + (['rates usd', 'rates non-usd'].includes(data.name.toLowerCase()) ? (data.contribution / totalRisk) * 100 : 0);
				}, 0);
			}
		)
	},
	topSpreadRisk: {
		extractor: new FactExtractor(
			[
				new StoreNestedFilterExtractor(
					['risk'],
					'risk.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'fixedIncome', desiredValueExpression: 'sleeveAnalysis.risk.factorContributors' }
					]
				)
			],
			(seriesData, contentService?) => {
				const maxSpreadRisk = seriesData.reduce((maxRisk, data) => {
					if (
						['spreads investment grade', 'spreads high yield', 'spreads municipal', 'spreads em', 'spreads securitized', 'spreads loans - credit'].includes(
							data.name.toLowerCase()
						)
					) {
						if (maxRisk) {
							return maxRisk.contribution >= data.contribution ? maxRisk : data;
						} else {
							return data;
						}
					}
					return maxRisk;
				}, null);
				const maxSpreadRiskKey = maxSpreadRisk ? 'RISK_' + maxSpreadRisk.name.replace(/[- ]/g, '_').toUpperCase() + '_FULL' : null;
				if (!maxSpreadRiskKey) {
					throw new Error('not any spread risk existed');
				}
				const result = contentService ? contentService.get(maxSpreadRiskKey, RiskFullNameDCR[maxSpreadRiskKey]) : RiskFullNameDCR[maxSpreadRiskKey];
				return result;
			})
	},
	fixedIncomeCount: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['details'],
				'details.portfolioBreakdowns',
				[
					{ propertyToCompare: 'name', comparedValue: 'assetClass', desiredValueExpression: 'sectors' }
				]
			)
		], sectors => {
			return sectors.find(sector => sector.name === 'Fixed Income') ? 1 : 0;
		})
	},
	fixedIncomeTotalRiskAndRateRiskLogic: {
		extractor: new FactExtractor(
			[
				new StoreNestedFilterExtractor(
					['risk'],
					'risk.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'fixedIncome', desiredValueExpression: 'sleeveAnalysis.risk.factorContributors' }
					]
				),
				new StoreNestedFilterExtractor(
					['risk'],
					'risk.sleeves',
					[
						{ propertyToCompare: 'sleeveId', comparedValue: 'fixedIncome', desiredValueExpression: 'sleeveAnalysis.risk.total' }
					]
				)
			],
			(factorContributors, totalRisk) => {
				const rateRisk = factorContributors.reduce((sum, data) => {
					return sum + (['rates usd', 'rates non-usd'].includes(data.name.toLowerCase()) ? (data.contribution / totalRisk) * 100 : 0);
				}, 0);
				const spreadRisk = factorContributors.reduce((sum, data) => {
					return (
						sum +
						(['spreads investment grade', 'spreads high yield', 'spreads municipal', 'spreads em', 'spreads securitized', 'spreads loans - credit'].includes(
							data.name.toLowerCase()
						)
							? (data.contribution / totalRisk) * 100
							: 0)
					);
				}, 0);
				if (rateRisk < 50 && spreadRisk < 25 && totalRisk * 100 >= 1.5) { return 0; }
				return 1;
			}
		)
	},
	equityRiskDecomposition: {
		extractor: new FactExtractor(
			[
				new LodashExtractor('risk.risk.factorContributors'),
				new LodashExtractor('risk.risk.total')
			],
			(factorContributors, totalRisk) => {
				const equityRisk = factorContributors.reduce((sum, data) => {
					return (
						sum +
						([
							'equity market',
							'united states',
							'equity country non us',
							'equity sector',
							'dividend yield',
							'size',
							'growth',
							'leverage',
							'momentum',
							'profitability',
							'value',
							'volatility',
							'earnings yield',
							'emerging',
							'liquidity',
							'oil',
							'reversal',
							'sentiment',
							'smallcap'
						].includes(data.name.toLowerCase())
							? data.contribution
							: 0)
					);
				}, 0);
				const relativeEquityRisk = (equityRisk / totalRisk) * 100;
				return relativeEquityRisk;
			})
	},
	totalRisk: {
		extractor: new FactExtractor([new LodashExtractor('risk.risk.total', 0)], totalRisk => {
			return totalRisk * 100;
		})
	},
	fixedIncomeTotalRisk: {
		extractor: new FactExtractor([
			new StoreNestedFilterExtractor(
				['risk'],
				'risk.sleeves',
				[
					{ propertyToCompare: 'sleeveId', comparedValue: 'fixedIncome', desiredValueExpression: 'sleeveAnalysis.risk.total' }
				]
			)
		], fixedIncomeRiskTotal => fixedIncomeRiskTotal * 100)
	},
	activeRiskBenchmark: {
		extractor: new FactExtractor([new LodashExtractor('risk.activePortfolioAnalysis.risk.total')], totalRisk => {
			return totalRisk * 100;
		})
	}
};
